import React, {useEffect, useState} from 'react'
import {Nullable, Optional} from '../../../../Common/TypeHelper'
import {NavigatorForMemberPage} from '../../../Navigator/NavigatorForMemberPage'
import MemberRepo from '../../../Repo/MemberRepo'
import {useLocation, useParams} from 'react-router-dom'
import {useAsync} from 'react-use'
import {MBMemberError} from '../../../Common/MBMemberError'
import {MailMagazineSelectContentBlock} from '../../Components/InputContentBlocks/MailMagazineSelectContentBlock'
import {BucketInfo} from '../../../DTO/BucketInfoDTO'
import {useMBMemberError} from '../../Hooks/UseMBMemberError'
import {MailMagazine} from '../../../DTO/MailMagazineDTO'
import {MembersPageTemplate} from '../../Molecules/BaseMembersPages/MembersPageTemplate'
import {PageTitle} from '../../Molecules/BaseMembersPages/PageTitle'
import StepGuide from '../../Molecules/StepGuide/StepGuide'
import {MessageForAction} from '../../Molecules/BaseMembersPages/MessageForAction'
import {PIMSecondaryButton} from '../../Atoms/Buttons/Bases/PIMSecondaryButton'
import {PIMSubmitButton} from '../../Atoms/Buttons/Bases/PIMSubmitButton'
import {PIMPageActions} from '../../Atoms/Layouts/PIMPageActions'
import {PIMStack} from '../../Atoms/Layouts/Bases/PIMStack'
import {Spacer} from '../../../../Common/Components/Spacer'
import {memberResponseStatusCheck} from '../../../Utils/MemberResponseStatusCheck'

type Props = {
  navigator: NavigatorForMemberPage
  memberRepo: MemberRepo
}

type LocationStateForMemberEditMailMagazinePage = {
  bucketInfo: BucketInfo
  subscribeMailMagazineIdList: number[]
}

export function MembersEditMailMagazineInputPage(props: Props): Nullable<JSX.Element> {
  const {bucketKey} = useParams<{ bucketKey: string }>()
  const location = useLocation<LocationStateForMemberEditMailMagazinePage>()
  const locationState: LocationStateForMemberEditMailMagazinePage = location.state

  const [bucketInfo, setBucketInfo] = useState<Optional<BucketInfo>>(locationState?.bucketInfo)
  const [subscribeMailMagazineIdList, setSubscribeMailMagazineIdList] =
    useState<number[]>(locationState?.subscribeMailMagazineIdList || [])

  const throwMBMemberError = useMBMemberError()

  const resultBucketInfo = useAsync(async () => {
    return await props.memberRepo.getBucketInfoByBucketKey(bucketKey)
  })
  const resultMemberBucketOptions = useAsync(async () => {
    return await props.memberRepo.getMemberBucketOptionsByBucketKey(bucketKey)
  })

  useEffect(() => {
    if (locationState) {
      return
    }
    if (!resultBucketInfo.value) {
      return
    }
    if (!resultBucketInfo.value.data) {
      return
    }
    if (!resultMemberBucketOptions.value) {
      return
    }
    if (!resultMemberBucketOptions.value.data) {
      return
    }
    const _subscribeMailMagazineIdList: number[] =
      resultMemberBucketOptions.value.data.subscribeMailMagazineIdList


    const filteredSubscribeMailMagazineIdList =
      resultBucketInfo.value.data.mailMagazineList
        .filter((item: MailMagazine) => _subscribeMailMagazineIdList.includes(item.id))
        .map((item: MailMagazine) => item.id)

    setBucketInfo({...resultBucketInfo.value.data})
    if (!locationState) {
      setSubscribeMailMagazineIdList(filteredSubscribeMailMagazineIdList)
    }

  }, [resultBucketInfo.value, resultMemberBucketOptions.value, locationState])

  if (isInvalidLocationState(locationState)) {
    throw new MBMemberError('InvalidOperation')
  }

  if (!locationState) {
    if (resultBucketInfo.loading) {
      return null
    }

    if (resultBucketInfo.error) {
      throw new MBMemberError(resultBucketInfo.error.message)
    }

    if (!resultBucketInfo.value) {
      throw new MBMemberError('UnknownError')
    }

    memberResponseStatusCheck(resultBucketInfo.value.status)

    if (!resultBucketInfo.value.data) {
      throw new MBMemberError('UnknownError')
    }

    if (resultMemberBucketOptions.loading) {
      return null
    }

    if (resultMemberBucketOptions.error) {
      throw new MBMemberError(resultMemberBucketOptions.error.message)
    }

    if (!resultMemberBucketOptions.value) {
      throw new MBMemberError('UnknownError')
    }

    memberResponseStatusCheck(resultMemberBucketOptions.value.status)

    if (!resultMemberBucketOptions.value.data) {
      throw new MBMemberError('UnknownError')
    }

    if (resultBucketInfo.value.data.mailMagazineList.length === 0) {
      throw new MBMemberError('NotFound')
    }
  }

  function isInvalidLocationState(
    locationState: LocationStateForMemberEditMailMagazinePage,
  ): boolean {
    if (!locationState) {
      return false
    }
    if (!locationState.bucketInfo) {
      return true
    }
    if (!locationState.subscribeMailMagazineIdList) {
      return true
    }
    // noinspection RedundantIfStatementJS
    if (!locationState.bucketInfo.mailMagazineList ||
      locationState.bucketInfo.mailMagazineList.length === 0) {
      return true
    }
    return false
  }

  function goToEditMailMagazineConfirmPage(): void {
    if (!bucketInfo) {
      throwMBMemberError('SystemError')
      return
    }
    props.navigator.goToEditMailMagazineConfirmPage(
      bucketKey,
      bucketInfo,
      subscribeMailMagazineIdList,
    )
  }

  if (!bucketInfo) {
    return null
  }

  return (
    <MembersPageTemplate hideFooterNavi={true} bucketInfo={bucketInfo}>
      <PageTitle>メルマガ情報変更</PageTitle>

      <StepGuide activity={'edit'} current={'enter'}/>

      <Spacer mb={'16px'}>
        <PIMStack alignItems={'center'}>
          <MailMagazineSelectContentBlock
            title={'メルマガ'}
            mailMagazineList={bucketInfo.mailMagazineList}
            mailMagazineIdCheckedList={subscribeMailMagazineIdList}
            onChange={(checkedIdList: number[]): void => {
              setSubscribeMailMagazineIdList(checkedIdList)
            }}
          />
        </PIMStack>
      </Spacer>

      <MessageForAction>
        上記の内容に間違いがなければ<br/>『変更内容の確認に進む』ボタンを押してください。
      </MessageForAction>

      <PIMPageActions>
        <PIMSubmitButton
          testId={'confirm-changes-button'}
          onClick={(): void => {
            goToEditMailMagazineConfirmPage()
          }}
        >
          変更内容の確認に進む
        </PIMSubmitButton>
        <PIMSecondaryButton
          onClick={(): void => {
            props.navigator.goToEditMailMagazineInfoPage(bucketKey)
          }}
        >
          メルマガ情報に戻る
        </PIMSecondaryButton>
      </PIMPageActions>
    </MembersPageTemplate>
  )
}
